import React, { useState } from "react";
import { X, RefreshCw, AlertTriangle, CheckCircle2, FileWarning, Ban, Link2, Users } from "lucide-react";
import api, { formatApiError } from "../lib/api";

// Column presets kept OUTSIDE the component so the arrays keep stable
// references across renders — otherwise every BucketTable would receive a
// brand-new `cols` array each render and lose memoization benefits.
const COLS_ALIAS = ["excel_name", "canonical_name", "matched_customer"];
const COLS_LINKS = ["product_name", "item_no", "reason"];
const COLS_INVOICED = ["po_number", "customer", "invoice_numbers", "row_count", "conflict"];
const COLS_CANCEL = ["po_number", "customer", "action"];
const COLS_MISSING = ["po_number", "customer", "reason"];
const COLS_NO_CHG = ["po_number", "customer"];

// Admin-only Excel → Order sync UI. Fetches Preview from Microsoft Graph,
// lets the user resolve ambiguities + confirm new customers/products, then
// runs Apply with a stale-preview guard (revalidate_sha256).
export default function SyncNowModal({ onClose, onCompleted }) {
    const [stage, setStage] = useState("idle"); // idle | previewing | preview | applying | done
    const [error, setError] = useState("");
    const [preview, setPreview] = useState(null);
    const [resolutions, setResolutions] = useState({}); // {po_normalized: {customer_id?, lines: [{row_idx, product_id?, create_product?}], production_type?}}
    const [result, setResult] = useState(null);

    const runPreview = async () => {
        setStage("previewing");
        setError("");
        try {
            const { data } = await api.post("/sync/purchase-orders/preview", null, {
                timeout: 180000,
            });
            setPreview(data);
            setStage("preview");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
            setStage("idle");
        }
    };

    const updateRes = (poNorm, updates) => {
        setResolutions((r) => ({ ...r, [poNorm]: { ...(r[poNorm] || {}), ...updates } }));
    };

    const runApply = async () => {
        setStage("applying");
        setError("");
        try {
            const { data } = await api.post("/sync/purchase-orders/apply", {
                sync_id: preview.sync_id || preview.id,
                resolutions,
                revalidate_sha256: preview.content_sha256,
            });
            setResult(data);
            setStage("done");
            onCompleted?.(data);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
            setStage("preview");
        }
    };

    const p = preview?.preview || {};
    const invoiced = preview?.invoiced_skipped || [];
    const meta = preview?.source_meta || {};

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
             data-testid="sync-now-modal">
            <div className="bg-white rounded-sm w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h2 className="text-lg font-bold flex items-center gap-2">
                        <RefreshCw className="w-5 h-5 text-red-700" />
                        Sync Purchase Orders from OneDrive
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-800"
                            data-testid="sync-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {error && (
                        <div className="text-sm border border-red-300 bg-red-50 text-red-800 rounded-sm px-3 py-2 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5" />
                            <div>{error}</div>
                        </div>
                    )}

                    {stage === "idle" && (
                        <div className="text-center py-10">
                            <p className="text-sm text-gray-600 mb-4">
                                Fetch the latest <b>PURCHASE ORDER LIST.xlsx</b> from OneDrive
                                and preview which POs will be imported, updated, cancelled,
                                or skipped. No records are created until you review + confirm.
                            </p>
                            <button
                                onClick={runPreview}
                                className="bg-red-700 hover:bg-red-800 text-white font-semibold rounded-sm px-6 py-2 inline-flex items-center gap-2"
                                data-testid="sync-run-preview"
                            >
                                <RefreshCw className="w-4 h-4" /> Fetch Preview
                            </button>
                        </div>
                    )}

                    {stage === "previewing" && (
                        <div className="text-center py-10 text-gray-600">
                            <RefreshCw className="w-6 h-6 animate-spin inline mr-2" />
                            Downloading workbook from Microsoft Graph…
                        </div>
                    )}

                    {(stage === "preview" || stage === "applying") && preview && (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                                <Stat label="Total rows" value={preview.total_rows} />
                                <Stat label="PO groups" value={preview.total_po_groups} />
                                <Stat label="Workbook" value={meta.filename}
                                      sub={meta.last_modified?.slice(0, 19)} />
                            </div>

                            {preview.status === "invalid_workbook" && (
                                <Section label="Workbook validation errors" color="red" icon={FileWarning}
                                         count={preview.validation_errors?.length}>
                                    <ul className="text-xs list-disc pl-4 space-y-1">
                                        {preview.validation_errors?.map((v) => <li key={v}>{v}</li>)}
                                    </ul>
                                </Section>
                            )}

                            <Section label="Customer Aliases Resolved" color="blue" icon={Users}
                                     count={p.alias_resolutions?.length}
                                     testid="sync-alias-resolutions">
                                <BucketTable
                                    pos={(p.alias_resolutions || []).map((x) => ({
                                        excel_name: x.excel_name,
                                        canonical_name: x.canonical_name,
                                        matched_customer: x.matched_customer_name || (x.candidates?.map((c) => c.name).join(" | ")) || "—",
                                    }))}
                                    cols={COLS_ALIAS}
                                />
                            </Section>

                            <Section label="Customer↔Product Links to Add" color="blue" icon={Link2}
                                     count={p.product_links_to_add?.length}
                                     testid="sync-links-to-add">
                                <BucketTable
                                    pos={(p.product_links_to_add || []).map((x) => ({
                                        product_name: x.product_name,
                                        item_no: x.item_no,
                                        reason: x.reason,
                                    }))}
                                    cols={COLS_LINKS}
                                />
                            </Section>

                            <Section label="Already Invoiced — Not Imported" color="gray" icon={Ban}
                                     count={invoiced.length}
                                     testid="sync-invoiced">
                                <BucketTable pos={invoiced.map((x) => ({
                                    po_number: x.po_number,
                                    customer: x.customer,
                                    invoice_numbers: x.invoice_numbers.join(", "),
                                    row_count: x.row_count,
                                    conflict: x.conflict ? "⚠ conflict" : "",
                                }))} cols={COLS_INVOICED} />
                            </Section>

                            <Section label="New Orders" color="green" icon={CheckCircle2}
                                     count={p.new_orders?.length}
                                     testid="sync-new-orders">
                                <PoEditableList
                                    pos={p.new_orders || []}
                                    resolutions={resolutions}
                                    onUpdate={updateRes}
                                />
                            </Section>

                            <Section label="Existing Orders — Update" color="amber" icon={AlertTriangle}
                                     count={p.existing_with_changes?.length}
                                     testid="sync-existing-with-changes">
                                <PoEditableList
                                    pos={p.existing_with_changes || []}
                                    resolutions={resolutions}
                                    onUpdate={updateRes}
                                    showDiffs
                                />
                            </Section>

                            <Section label="Cancelled" color="red" icon={Ban}
                                     count={p.cancelled_pos?.length}
                                     testid="sync-cancelled">
                                <BucketTable
                                    pos={(p.cancelled_pos || []).map((x) => ({
                                        po_number: x.po_number,
                                        customer: x.customer?.excel_name,
                                        action: x.action,
                                    }))}
                                    cols={COLS_CANCEL}
                                />
                            </Section>

                            <Section label="Missing QTY" color="amber" icon={FileWarning}
                                     count={p.missing_qty?.length}
                                     testid="sync-missing-qty">
                                <BucketTable
                                    pos={(p.missing_qty || []).map((x) => ({
                                        po_number: x.po_number,
                                        customer: x.customer?.excel_name,
                                        reason: x.reason,
                                    }))}
                                    cols={COLS_MISSING}
                                />
                            </Section>

                            <Section label="No Changes" color="gray" icon={CheckCircle2}
                                     count={p.no_changes?.length}
                                     testid="sync-no-changes">
                                <BucketTable
                                    pos={(p.no_changes || []).map((x) => ({
                                        po_number: x.po_number,
                                        customer: x.customer?.excel_name,
                                    }))}
                                    cols={COLS_NO_CHG}
                                />
                            </Section>
                        </>
                    )}

                    {stage === "done" && result && (
                        <div className="border border-green-300 bg-green-50 rounded-sm p-4 space-y-2">
                            <div className="font-bold text-green-900 inline-flex items-center gap-2">
                                <CheckCircle2 className="w-5 h-5" /> Sync applied
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                                <Stat label="Orders created" value={result.orders_created} />
                                <Stat label="Orders updated" value={result.orders_updated} />
                                <Stat label="Orders cancelled" value={result.orders_cancelled} />
                                <Stat label="Customers created" value={result.customers_created} />
                                <Stat label="Products created" value={result.products_created} />
                                <Stat label="Line items created" value={result.line_items_created} />
                                <Stat label="Product links created" value={result.product_links_created} />
                            </div>
                            {result.errors?.length > 0 && (
                                <div className="text-xs text-red-800">
                                    <b>Errors:</b>
                                    <ul className="list-disc pl-4">
                                        {result.errors.map((e) => <li key={`err-${e}`}>{e}</li>)}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="border-t border-gray-200 p-3 flex justify-end gap-2 bg-gray-50">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700"
                            data-testid="sync-cancel">
                        Close
                    </button>
                    {stage === "preview" && (p.new_orders?.length || p.existing_with_changes?.length || p.cancelled_pos?.length) ? (
                        <button
                            onClick={runApply}
                            className="bg-red-700 hover:bg-red-800 text-white font-semibold rounded-sm px-6 py-2"
                            data-testid="sync-apply"
                        >
                            Apply Import ({(p.new_orders?.length || 0) + (p.existing_with_changes?.length || 0) + (p.cancelled_pos?.length || 0)})
                        </button>
                    ) : null}
                    {stage === "applying" && (
                        <button disabled className="bg-gray-300 text-gray-600 rounded-sm px-6 py-2">
                            Applying…
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, sub }) {
    return (
        <div className="bg-gray-50 border border-gray-200 rounded-sm p-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
            <div className="text-sm font-semibold odr-mono">{value ?? "—"}</div>
            {sub && <div className="text-[10px] text-gray-500 odr-mono">{sub}</div>}
        </div>
    );
}

function Section({ label, count, color, icon: Icon, children, testid }) {
    const [open, setOpen] = useState(true);
    if (!count) return null;
    const colors = {
        red: "border-red-300 bg-red-50",
        amber: "border-amber-300 bg-amber-50",
        green: "border-emerald-300 bg-emerald-50",
        gray: "border-gray-300 bg-gray-50",
        blue: "border-sky-300 bg-sky-50",
    };
    return (
        <div className={`border rounded-sm ${colors[color] || colors.gray}`} data-testid={testid}>
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 p-2 text-left text-sm font-semibold"
            >
                {Icon && <Icon className="w-4 h-4" />}
                {label} · {count}
                <span className="ml-auto text-xs text-gray-500">{open ? "▼" : "▶"}</span>
            </button>
            {open && <div className="p-2 bg-white">{children}</div>}
        </div>
    );
}

function BucketTable({ pos, cols }) {
    if (!pos.length) return <div className="text-xs text-gray-500 p-2">None.</div>;
    return (
        <table className="odr-table text-xs">
            <thead>
                <tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr>
            </thead>
            <tbody>
                {pos.map((r, i) => (
                    <tr key={r.po_number || r.product_name || r.excel_name || `row-${i}`}>
                        {cols.map((c) => <td key={c}>{r[c] ?? "—"}</td>)}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function PoEditableList({ pos, resolutions, onUpdate, showDiffs }) {
    if (!pos.length) return <div className="text-xs text-gray-500 p-2">None.</div>;
    return (
        <div className="space-y-2">
            {pos.map((po) => {
                const res = resolutions[po.po_normalized] || {};
                const cust = po.customer || {};
                return (
                    <div key={po.po_normalized}
                         className="border border-gray-200 rounded-sm p-2 text-xs"
                         data-testid={`sync-po-${po.po_normalized}`}>
                        <div className="flex items-center justify-between mb-1">
                            <div className="font-semibold odr-mono">{po.po_number}</div>
                            <div className="text-[10px] uppercase text-gray-500">{po.action}</div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-1">
                            <div>
                                <b>Customer:</b> {cust.excel_name}
                                {cust.alias_applied && cust.canonical_name && (
                                    <span className="ml-1 text-[10px] text-sky-800 bg-sky-100 border border-sky-200 rounded-sm px-1 py-0.5"
                                          data-testid={`sync-alias-badge-${po.po_normalized}`}>
                                        → {cust.canonical_name}
                                    </span>
                                )}
                            </div>
                            <div><b>Status:</b> {cust.status}
                                {cust.status === "ambiguous" && (
                                    <select
                                        className="ml-1 border rounded-sm px-1"
                                        value={res.customer_id || ""}
                                        onChange={(e) => onUpdate(po.po_normalized, { customer_id: e.target.value })}
                                        data-testid={`sync-cust-select-${po.po_normalized}`}
                                    >
                                        <option value="">Choose…</option>
                                        {cust.candidates.map((c) => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div><b>Receive:</b> {po.receive_date}</div>
                            <div><b>Due:</b> {po.due_date}</div>
                        </div>
                        {showDiffs && po.diffs?.length > 0 && (
                            <div className="text-[11px] bg-amber-50 border border-amber-200 rounded-sm p-1 mb-1">
                                <b>Changes:</b>{" "}
                                {po.diffs.map((d, i) => (
                                    <span key={d.field}>
                                        {d.field}: {String(d.old ?? "—")} → <b>{String(d.new)}</b>
                                        {i < po.diffs.length - 1 ? "; " : ""}
                                    </span>
                                ))}
                            </div>
                        )}
                        <table className="odr-table text-[11px]">
                            <thead>
                                <tr>
                                    <th>Item</th><th>Description</th>
                                    <th className="text-right">Qty</th>
                                    <th className="text-right">Price</th>
                                    <th>Product</th>
                                </tr>
                            </thead>
                            <tbody>
                                {po.lines.map((l) => (
                                    <tr key={l.row_idx}>
                                        <td className="odr-mono">{l.item_no}</td>
                                        <td>{l.item_description}</td>
                                        <td className="text-right odr-mono">{l.qty}</td>
                                        <td className="text-right odr-mono">{l.price_ea}</td>
                                        <td>
                                            <span className={
                                                l.product_status === "matched" ? "text-emerald-700" :
                                                l.product_status === "matched_needs_link" ? "text-sky-700" :
                                                l.product_status === "ambiguous" ? "text-amber-700" :
                                                "text-gray-600"
                                            }>
                                                {l.product_status === "matched_needs_link" ? "link existing" : l.product_status}
                                            </span>
                                            {l.product_status === "ambiguous" && (
                                                <select
                                                    className="ml-1 border rounded-sm px-1"
                                                    value={(res.lines?.find((x) => x.row_idx === l.row_idx) || {}).product_id || ""}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        const lines = (res.lines || []).filter((x) => x.row_idx !== l.row_idx);
                                                        lines.push({ row_idx: l.row_idx, product_id: val });
                                                        onUpdate(po.po_normalized, { lines });
                                                    }}
                                                    data-testid={`sync-prod-select-${po.po_normalized}-${l.row_idx}`}
                                                >
                                                    <option value="">Choose…</option>
                                                    {l.product_candidates.map((p) => (
                                                        <option key={p.id} value={p.id}>{p.name}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            })}
        </div>
    );
}
